import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Clock3,
  Package,
  PackageCheck,
  RefreshCw,
  Truck,
} from 'lucide-react'

import { getDashboard } from '@/api/dashboard'
import { giacenzeProdottiApi, giacenzeBoxApi } from '@/api/magazzino'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import useAuth from '@hooks/useAuth'


const URGENZA_STYLES = {
  inizio: {
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    row: 'border-amber-200 bg-amber-50/60',
  },
  in_corso: {
    badge: 'bg-orange-100 text-orange-800 border-orange-200',
    row: 'border-orange-200 bg-orange-50/60',
  },
  ultimo_giorno: {
    badge: 'bg-red-100 text-red-800 border-red-200',
    row: 'border-red-300 bg-red-50',
  },
  scaduto: {
    badge: 'bg-red-700 text-white border-red-700',
    row: 'border-red-400 bg-red-100/70',
  },
}


function formatDate(value) {
  if (!value) return '-'
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(`${value}T12:00:00`))
}


function formatGeneratedAt(value) {
  if (!value) return ''
  return new Intl.DateTimeFormat('it-IT', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function formatCurrencyFromCents(cents) {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100)
}


function KpiCard({ title, value, description, icon: Icon, color, loading }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <div className={`rounded-lg p-2 ${color}`}>
          <Icon className="h-4 w-4 text-white" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{loading ? '—' : value}</div>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  )
}


export default function Dashboard() {
  const { user } = useAuth()
  const [valoreMetrica, setValoreMetrica] = useState('costo')
  const {
    data,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['dashboard-operativa'],
    queryFn: () => getDashboard().then((response) => response.data),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  })

  const {
    data: valoreProdotti,
    isPending: prodottiValuePending,
    isFetching: prodottiValueFetching,
    isError: prodottiValueError,
    refetch: refetchValoreProdotti,
  } = useQuery({
    queryKey: ['giacenze-prodotti', 'valore-vista', 'dashboard', valoreMetrica],
    queryFn: () => giacenzeProdottiApi.valoreVista({ metrica: valoreMetrica }).then((response) => response.data),
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
  const {
    data: valoreBox,
    isPending: boxValuePending,
    isFetching: boxValueFetching,
    isError: boxValueError,
    refetch: refetchValoreBox,
  } = useQuery({
    queryKey: ['giacenze-box', 'valore-vista', 'dashboard', valoreMetrica],
    queryFn: () => giacenzeBoxApi.valoreVista({ metrica: valoreMetrica }).then((response) => response.data),
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })

  const inventoryRows = [
    {
      label: 'Prodotti',
      metricLabel: valoreMetrica === 'costo' ? 'Costo di acquisto' : 'Prezzo di vendita',
      value: valoreProdotti,
      pending: prodottiValuePending,
      error: prodottiValueError,
    },
    {
      label: 'Box assemblate',
      metricLabel: valoreMetrica === 'costo' ? 'Costo di composizione' : 'Prezzo di vendita',
      value: valoreBox,
      pending: boxValuePending,
      error: boxValueError,
    },
  ]

  const ordini = data?.ordini
  const scadenze = ordini?.in_scadenza
  const sottoscorta = data?.prodotti?.sottoscorta
  const kpiData = [
    {
      title: 'Ordini ricevuti',
      value: ordini?.ricevuti ?? 0,
      description: 'Escluse bozze e annullati',
      icon: ClipboardList,
      color: 'bg-blue-600',
    },
    {
      title: 'Da preparare',
      value: ordini?.da_preparare ?? 0,
      description: 'Confermati o in preparazione',
      icon: Clock3,
      color: 'bg-amber-500',
    },
    {
      title: 'Pronti',
      value: ordini?.pronti ?? 0,
      description: 'Pronti per la spedizione',
      icon: PackageCheck,
      color: 'bg-emerald-600',
    },
    {
      title: 'Spediti',
      value: ordini?.spediti ?? 0,
      description: 'Inclusi evasi e chiusi',
      icon: Truck,
      color: 'bg-violet-600',
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Benvenuto, {user?.first_name || user?.username}
          </h1>
          <p className="mt-1 text-muted-foreground">
            Situazione operativa aggiornata
            {data?.generato_il ? ` alle ${formatGeneratedAt(data.generato_il)}` : ''}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            refetch()
            refetchValoreProdotti()
            refetchValoreBox()
          }}
          disabled={isFetching || prodottiValueFetching || boxValueFetching}
          aria-label="Aggiorna dashboard"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching || prodottiValueFetching || boxValueFetching ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>

      {isError && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="flex flex-col items-start gap-3 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-semibold text-red-900">Dashboard temporaneamente non disponibile</p>
              <p className="text-sm text-red-700">I dati operativi non sono stati caricati.</p>
            </div>
            <Button type="button" variant="outline" onClick={() => refetch()}>
              Riprova
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {kpiData.map((kpi) => (
          <KpiCard key={kpi.title} {...kpi} loading={isLoading} />
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Valore economico del magazzino</CardTitle>
              <CardDescription>Prodotti e box assemblate in tutto il magazzino</CardDescription>
            </div>
            <div className="w-full sm:w-[220px]">
              <Select value={valoreMetrica} onValueChange={setValoreMetrica}>
                <SelectTrigger aria-label="Metrica valore magazzino">
                  <SelectValue placeholder="Metrica valore" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="costo">Al costo</SelectItem>
                  <SelectItem value="vendita">Al prezzo di vendita</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-4 xl:grid-cols-3">
            {[
              { key: 'disponibile', title: 'Valore fisicamente disponibile', color: 'text-emerald-600' },
              { key: 'riservata', title: 'Valore impegnato negli ordini', color: 'text-amber-600' },
              { key: 'effettiva', title: 'Valore libero per nuovi ordini', color: 'text-primary' },
            ].map(({ key, title, color }) => (
              <div key={key} className="rounded-lg border p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{title}</p>
                <div className="mt-3 divide-y">
                  {inventoryRows.map((row) => {
                    const cents = row.value?.valore_cents?.[key]
                    return (
                      <div key={row.label} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-2 first:pt-0 last:pb-0">
                        <div>
                          <p className="text-sm font-medium">{row.label}</p>
                          <p className="text-xs text-muted-foreground">
                            {row.metricLabel} · {row.pending || row.error ? '—' : row.value?.quantita?.[key] ?? '—'} unità
                          </p>
                        </div>
                        <p className={`text-lg font-semibold tabular-nums ${color}`}>
                          {row.pending ? '…' : row.error || typeof cents !== 'number' ? 'N/D' : formatCurrencyFromCents(cents)}
                        </p>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Prodotti e box sono mostrati separatamente: il valore delle box può già includere i prodotti usati per assemblarle.
          </p>
          {(prodottiValueError || boxValueError) && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
              <p className="text-xs text-destructive">Uno o più valori del magazzino non sono disponibili.</p>
              <Button type="button" variant="outline" size="sm" onClick={() => {
                if (prodottiValueError) refetchValoreProdotti()
                if (boxValueError) refetchValoreBox()
              }}>
                Riprova valori
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                  Ordini in scadenza
                </CardTitle>
                <CardDescription>
                  Consegne scadute o con inizio entro i prossimi 3 giorni
                </CardDescription>
              </div>
              <Badge variant={scadenze?.totale > 0 ? 'destructive' : 'success'}>
                {isLoading ? 'Caricamento' : `${scadenze?.totale ?? 0} ordini`}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Caricamento scadenze…</p>
            ) : scadenze?.items?.length > 0 ? (
              <div className="space-y-2">
                {scadenze.items.map((ordine) => {
                  const style = URGENZA_STYLES[ordine.urgenza] || URGENZA_STYLES.inizio
                  return (
                    <Link
                      key={ordine.id}
                      to={`/ordini/${ordine.id}`}
                      className={`flex flex-col gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/60 sm:flex-row sm:items-center sm:justify-between ${style.row}`}
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-sm font-semibold">{ordine.numero}</span>
                          <Badge variant="outline" className={style.badge}>
                            {ordine.urgenza_display}
                          </Badge>
                        </div>
                        <p className="mt-1 truncate text-sm font-medium">{ordine.cliente}</p>
                        <p className="text-xs text-muted-foreground">
                          {ordine.stato_display} · Consegna {formatDate(ordine.data_consegna_dal)}–{formatDate(ordine.data_consegna_al)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 self-end sm:self-auto">
                        {ordine.giorni_oltre_scadenza > 0 && (
                          <span className="text-xs font-semibold text-red-800">
                            {ordine.giorni_oltre_scadenza} {ordine.giorni_oltre_scadenza === 1 ? 'giorno' : 'giorni'} oltre
                          </span>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </Link>
                  )
                })}
                {scadenze.totale > scadenze.items.length && (
                  <Button asChild variant="link" className="px-0">
                    <Link to="/ordini">Vedi tutti gli ordini</Link>
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="mb-3 rounded-full bg-emerald-100 p-3">
                  <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                </div>
                <p className="text-sm font-medium text-emerald-700">Nessun ordine in scadenza</p>
                <p className="mt-1 text-xs text-muted-foreground">Nessun ordine scaduto o in scadenza nei prossimi 3 giorni</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-orange-600" />
                  Alert scorte
                </CardTitle>
                <CardDescription>Prodotti sotto la soglia minima</CardDescription>
              </div>
              <Badge variant={sottoscorta?.totale > 0 ? 'destructive' : 'success'}>
                {isLoading ? '…' : sottoscorta?.totale ?? 0}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Caricamento scorte…</p>
            ) : sottoscorta?.items?.length > 0 ? (
              <div className="space-y-2">
                {sottoscorta.items.map((prodotto) => (
                  <div key={prodotto.id} className="rounded-lg border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{prodotto.nome}</p>
                        <p className="text-xs text-muted-foreground">
                          {prodotto.codice} · {prodotto.categoria}
                        </p>
                      </div>
                      <Badge variant={prodotto.livello === 'critico' ? 'destructive' : 'warning'}>
                        {prodotto.quantita_effettiva} / {prodotto.soglia_minima}
                      </Badge>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Disponibili {prodotto.quantita_disponibile} · Riservati {prodotto.quantita_riservata}
                    </p>
                  </div>
                ))}
                <Button asChild variant="outline" size="sm" className="mt-2 w-full">
                  <Link to="/prodotti">Apri prodotti</Link>
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="mb-3 rounded-full bg-emerald-100 p-3">
                  <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                </div>
                <p className="text-sm font-medium text-emerald-700">Scorte regolari</p>
                <p className="mt-1 text-xs text-muted-foreground">Nessun prodotto sotto soglia</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
